import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';

const DEFAULT_DESIGNATION = 'Property Consultant';
const DEFAULT_DEEPLINKS = {
  fixProfileUrl: '/account',
  fixBranchUrl: '/account',
};

const normalizeString = (value) => {
  if (value === null || value === undefined) return null;
  const text = String(value).trim();
  return text ? text : null;
};

const normalizeEmailPrefix = (email) => {
  const normalized = normalizeString(email);
  if (!normalized) return null;
  const parts = normalized.split('@');
  return parts[0] ? parts[0].trim() : null;
};

const resolveChain = (entries) => {
  for (const entry of entries) {
    const normalized = normalizeString(entry.value);
    if (normalized) {
      return {
        value: normalized,
        source: entry.source,
        derived: Boolean(entry.derived),
      };
    }
  }
  return { value: null, source: null, derived: false };
};

const resolveFullName = ({ userDoc, onboardingRequest, agreement, authUser }) => {
  const emailPrefix =
    normalizeEmailPrefix(authUser?.email) ||
    normalizeEmailPrefix(onboardingRequest?.assignedEmail);

  return resolveChain([
    { value: userDoc?.fullName, source: 'users.fullName' },
    { value: onboardingRequest?.fullName, source: 'onboardingRequests.fullName' },
    { value: agreement?.formData?.fullName, source: 'franchiseAgreements.formData.fullName' },
    { value: authUser?.displayName, source: 'authUser.displayName', derived: true },
    { value: authUser?.username, source: 'authUser.username', derived: true },
    { value: emailPrefix, source: 'emailPrefix', derived: true },
  ]);
};

const resolveEmail = ({ authUser, onboardingRequest }) =>
  resolveChain([
    { value: authUser?.email, source: 'authUser.email' },
    { value: onboardingRequest?.assignedEmail, source: 'onboardingRequests.assignedEmail' },
  ]);

const resolveDesignation = ({ userDoc, agreement }) => {
  const resolved = resolveChain([
    { value: userDoc?.designation, source: 'users.designation' },
    { value: agreement?.formData?.designation, source: 'franchiseAgreements.formData.designation' },
  ]);
  return {
    value: resolved.value || DEFAULT_DESIGNATION,
    source: resolved.value ? resolved.source : 'default',
  };
};

const resolvePhone = ({ userDoc, onboardingRequest, agreement }) =>
  resolveChain([
    { value: userDoc?.phone, source: 'users.phone' },
    { value: onboardingRequest?.phone, source: 'onboardingRequests.phone' },
    { value: agreement?.formData?.phone, source: 'franchiseAgreements.formData.phone' },
  ]);

const resolveWhatsapp = ({ userDoc, phone }) =>
  resolveChain([
    { value: userDoc?.whatsapp, source: 'users.whatsapp' },
    { value: phone, source: 'userProfile.phone', derived: true },
  ]);

const resolvePhotoUrl = ({ userDoc, authUser }) =>
  resolveChain([
    { value: userDoc?.photoUrl, source: 'users.photoUrl' },
    { value: authUser?.photoURL, source: 'authUser.photoURL', derived: true },
  ]);

const normalizeSocialLinks = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }
  return {
    instagram: normalizeString(value.instagram),
    facebook: normalizeString(value.facebook),
    youtube: normalizeString(value.youtube),
    website: normalizeString(value.website),
  };
};

const resolveRole = ({ userDoc, authUser }) => {
  const roleFromRoles = (roles) => {
    if (!roles || typeof roles !== 'object') return null;
    const keys = Object.keys(roles);
    return keys.length > 0 ? keys[0] : null;
  };
  return (
    normalizeString(userDoc?.role) ||
    normalizeString(roleFromRoles(userDoc?.roles)) ||
    normalizeString(authUser?.role) ||
    normalizeString(roleFromRoles(authUser?.roles)) ||
    null
  );
};

const resolveTenantIdWithSource = ({
  userDoc,
  onboardingRequest,
  agreement,
  franchiseOwner,
  franchiseDoc,
  franchiseOnboarding,
  authUser,
  tenantIdOverride,
}) =>
  resolveChain([
    { value: tenantIdOverride, source: 'tenantIdOverride' },
    { value: userDoc?.tenantId, source: 'users.tenantId' },
    { value: onboardingRequest?.tenantId, source: 'onboardingRequests.tenantId' },
    { value: agreement?.tenantId, source: 'franchiseAgreements.tenantId' },
    { value: franchiseOwner?.tenantId, source: 'franchiseOwners.tenantId' },
    { value: franchiseDoc?.tenantId, source: 'franchises.tenantId' },
    { value: franchiseOnboarding?.tenantId, source: 'franchiseOnboarding.tenantId' },
    { value: authUser?.tenantId, source: 'authUser.tenantId' },
  ]);

const resolveTenantId = (params) => resolveTenantIdWithSource(params).value;

const resolveBranchIdWithSource = ({
  userDoc,
  onboardingRequest,
  agreement,
  franchiseOwner,
  franchiseDoc,
  franchiseOnboarding,
  branchIdOverride,
}) =>
  resolveChain([
    { value: branchIdOverride, source: 'branchIdOverride' },
    { value: userDoc?.branchId, source: 'users.branchId' },
    { value: onboardingRequest?.branchId, source: 'onboardingRequests.branchId' },
    { value: agreement?.branchId, source: 'franchiseAgreements.branchId' },
    { value: agreement?.franchiseId, source: 'franchiseAgreements.franchiseId' },
    { value: franchiseOwner?.franchiseId, source: 'franchiseOwners.franchiseId' },
    { value: franchiseOnboarding?.franchiseId, source: 'franchiseOnboarding.franchiseId' },
    { value: franchiseDoc?.id, source: 'franchises.id' },
  ]);

const resolveBranchId = (params) => resolveBranchIdWithSource(params).value;

const buildAddressFromOnboarding = (franchiseOnboarding) => {
  const address = franchiseOnboarding?.personalDetails?.address || {};
  const parts = [
    normalizeString(address.street),
    normalizeString(address.city),
    normalizeString(address.state),
  ].filter(Boolean);
  const postal = normalizeString(address.pincode);
  if (postal) {
    parts.push(postal);
  }
  return parts.length > 0 ? parts.join(', ') : null;
};

const buildAddressFromAgreement = (agreement) => {
  const formData = agreement?.formData || {};
  const parts = [
    normalizeString(formData.addressLine1),
    normalizeString(formData.addressLine2),
    normalizeString(formData.city),
    normalizeString(formData.state),
  ].filter(Boolean);
  const postal = normalizeString(formData.pincode);
  if (postal) {
    parts.push(postal);
  }
  return parts.length > 0 ? parts.join(', ') : null;
};

const buildBranchProfile = ({ franchiseDoc, franchiseOnboarding, agreement, userDoc }) => {
  const formData = agreement?.formData || {};
  const onboardingAddress = buildAddressFromOnboarding(franchiseOnboarding);
  const agreementAddress = buildAddressFromAgreement(agreement);
  const address = resolveChain([
    { value: onboardingAddress, source: 'franchiseOnboarding.personalDetails.address' },
    { value: agreementAddress, source: 'franchiseAgreements.formData.address' },
  ]).value;

  const city = resolveChain([
    { value: franchiseOnboarding?.territory?.primaryCity, source: 'franchiseOnboarding.territory.primaryCity' },
    { value: franchiseOnboarding?.personalDetails?.address?.city, source: 'franchiseOnboarding.personalDetails.address.city' },
    { value: formData.city, source: 'franchiseAgreements.formData.city' },
    { value: formData.preferredLocation, source: 'franchiseAgreements.formData.preferredLocation' },
  ]).value;

  const branchName = resolveChain([
    { value: franchiseDoc?.businessName, source: 'franchises.businessName' },
    { value: franchiseDoc?.branchName, source: 'franchises.branchName' },
    { value: formData.companyName, source: 'franchiseAgreements.formData.companyName' },
    { value: formData.businessName, source: 'franchiseAgreements.formData.businessName' },
    { value: userDoc?.organization, source: 'users.organization' },
    { value: userDoc?.company, source: 'users.company' },
  ]).value;

  const reraId = resolveChain([
    { value: formData.reraNumber, source: 'franchiseAgreements.formData.reraNumber' },
    { value: userDoc?.licenseNumber, source: 'users.licenseNumber' },
  ]).value;

  const branchPhone = resolveChain([
    { value: franchiseOnboarding?.personalDetails?.phone, source: 'franchiseOnboarding.personalDetails.phone' },
    { value: formData.phone, source: 'franchiseAgreements.formData.phone' },
    { value: userDoc?.phone, source: 'users.phone' },
  ]).value;

  const website = resolveChain([
    { value: userDoc?.socialLinks?.website, source: 'users.socialLinks.website' },
    { value: userDoc?.website, source: 'users.website' },
  ]).value;

  return {
    branchName: branchName || null,
    address: address || 'Not set',
    city: city || null,
    reraId: reraId || null,
    branchPhone: branchPhone || null,
    website: website || null,
  };
};

const buildMissingFields = ({ userProfile, branchProfile, fullNameDerived }) => {
  const missing = new Set();
  if (!userProfile.fullName || fullNameDerived) missing.add('userProfile.fullName');
  if (!userProfile.designation) missing.add('userProfile.designation');
  if (!userProfile.phone) missing.add('userProfile.phone');
  if (!userProfile.whatsapp) missing.add('userProfile.whatsapp');
  if (!userProfile.email) missing.add('userProfile.email');
  if (!userProfile.photoUrl) missing.add('userProfile.photoUrl');

  if (!branchProfile.branchName) missing.add('branchProfile.branchName');
  if (!branchProfile.address || branchProfile.address === 'Not set') {
    missing.add('branchProfile.address');
  }
  if (!branchProfile.city) missing.add('branchProfile.city');
  if (!branchProfile.reraId) missing.add('branchProfile.reraId');
  if (!branchProfile.branchPhone) missing.add('branchProfile.branchPhone');
  if (!branchProfile.website) missing.add('branchProfile.website');

  return Array.from(missing);
};

const buildBrandingContextFromDocs = ({
  authUser,
  userDoc,
  onboardingRequest,
  agreement,
  franchiseOwner,
  franchiseDoc,
  franchiseOnboarding,
  tenantIdOverride,
  branchIdOverride,
  readError,
}) => {
  const tenantResolved = resolveTenantIdWithSource({
    userDoc,
    onboardingRequest,
    agreement,
    franchiseOwner,
    franchiseDoc,
    franchiseOnboarding,
    authUser,
    tenantIdOverride,
  });

  const branchResolved = resolveBranchIdWithSource({
    userDoc,
    onboardingRequest,
    agreement,
    franchiseOwner,
    franchiseDoc,
    franchiseOnboarding,
    branchIdOverride,
  });
  const tenantId = tenantResolved.value;
  const branchId = branchResolved.value;

  const fullNameResolved = resolveFullName({
    userDoc,
    onboardingRequest,
    agreement,
    authUser,
  });
  const emailResolved = resolveEmail({ authUser, onboardingRequest });
  const designationResolved = resolveDesignation({ userDoc, agreement });
  const phoneResolved = resolvePhone({ userDoc, onboardingRequest, agreement });
  const whatsappResolved = resolveWhatsapp({ userDoc, phone: phoneResolved.value });
  const photoResolved = resolvePhotoUrl({ userDoc, authUser });
  const socialLinks = normalizeSocialLinks(userDoc?.socialLinks);
  const role = resolveRole({ userDoc, authUser });

  const userProfile = {
    fullName: fullNameResolved.value,
    designation: designationResolved.value,
    phone: phoneResolved.value,
    whatsapp: whatsappResolved.value,
    email: emailResolved.value,
    photoUrl: photoResolved.value,
    socialLinks,
  };

  const branchProfile = buildBranchProfile({
    franchiseDoc,
    franchiseOnboarding,
    agreement,
    userDoc,
  });

  const blockingReasons = [];
  if (!tenantId) blockingReasons.push('TENANT_NOT_RESOLVED');
  if (!branchId) blockingReasons.push('BRANCH_NOT_RESOLVED');
  if (readError) blockingReasons.push('CONTEXT_RESOLUTION_FAILED');

  const missingFields = buildMissingFields({
    userProfile,
    branchProfile,
    fullNameDerived: fullNameResolved.derived,
  });

  return {
    tenantId,
    branchId,
    role,
    userProfile,
    branchProfile,
    derived: {
      displayName: fullNameResolved.value,
      displayEmail: emailResolved.value,
    },
    missingFields,
    blockingReasons,
    deepLinks: { ...DEFAULT_DEEPLINKS },
    resolutionSources: {
      tenantId: tenantResolved.source || null,
      branchId: branchResolved.source || null,
    },
  };
};

const getFirestore = () => {
  try {
    return firebase.firestore();
  } catch (error) {
    console.error('BrandingContext: Firestore unavailable', error);
    return null;
  }
};

const safeGetDoc = async (ref, onError) => {
  try {
    const snapshot = await ref.get();
    if (!snapshot.exists) return null;
    return { id: snapshot.id, ...snapshot.data() };
  } catch (error) {
    onError(error);
    return null;
  }
};

const safeGetFirst = async (query, onError) => {
  try {
    const snapshot = await query.get();
    if (snapshot.empty) return null;
    const doc = snapshot.docs[0];
    return { id: doc.id, ...doc.data() };
  } catch (error) {
    onError(error);
    return null;
  }
};

const safeGetSnapshot = async (promise, onError) => {
  try {
    return await promise;
  } catch (error) {
    onError(error);
    return null;
  }
};

const resolveLatestAgreement = (snapshot) => {
  if (!snapshot || snapshot.empty) return null;
  const docs = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  docs.sort((a, b) => {
    const timeA = a.createdAt?.toMillis?.() || 0;
    const timeB = b.createdAt?.toMillis?.() || 0;
    return timeB - timeA;
  });
  return docs[0] || null;
};

export const getBrandingContext = async (authUser) => {
  let readError = false;
  const reportError = (error) => {
    console.error('BrandingContext: read error', error);
    readError = true;
  };

  if (!authUser) {
    return buildBrandingContextFromDocs({
      authUser: null,
      readError: true,
    });
  }

  const db = getFirestore();
  if (!db) {
    return buildBrandingContextFromDocs({
      authUser,
      readError: true,
    });
  }

  let onboardingRequest = null;
  if (authUser.email) {
    onboardingRequest = await safeGetFirst(
      db.collection('onboardingRequests').where('assignedEmail', '==', authUser.email).limit(1),
      reportError,
    );
  }
  if (!onboardingRequest && authUser.uid) {
    onboardingRequest = await safeGetDoc(
      db.collection('onboardingRequests').doc(authUser.uid),
      reportError,
    );
  }

  const effectiveUserId = onboardingRequest?.userId || onboardingRequest?.id || authUser.uid || null;
  const effectiveUserEmail =
    onboardingRequest?.userEmail || onboardingRequest?.email || authUser.email || null;

  const userDoc = effectiveUserId
    ? await safeGetDoc(db.collection('users').doc(effectiveUserId), reportError)
    : null;

  const scopedTenantId =
    normalizeString(userDoc?.tenantId) ||
    normalizeString(onboardingRequest?.tenantId) ||
    normalizeString(authUser?.tenantId) ||
    null;

  const canReadFranchiseOwner =
    effectiveUserId &&
    (effectiveUserId === authUser.uid || authUser?.email === 'jeethbuildcon@gmail.com');

  let franchiseOwner = canReadFranchiseOwner
    ? await safeGetDoc(db.collection('franchiseOwners').doc(effectiveUserId), reportError)
    : null;
  if (
    scopedTenantId &&
    franchiseOwner?.tenantId &&
    normalizeString(franchiseOwner.tenantId) !== scopedTenantId
  ) {
    reportError(new Error('Franchise owner tenant mismatch'));
    franchiseOwner = null;
  }

  let agreement = null;
  if (effectiveUserId) {
    let agreementQuery = db
      .collection('franchiseAgreements')
      .where('userId', '==', effectiveUserId);
    if (scopedTenantId) {
      agreementQuery = agreementQuery.where('tenantId', '==', scopedTenantId);
    }
    const agreementSnapshot = await safeGetSnapshot(agreementQuery.get(), reportError);
    agreement = resolveLatestAgreement(agreementSnapshot);
  }

  if (!agreement && effectiveUserEmail) {
    let fallbackQuery = db
      .collection('franchiseAgreements')
      .where('userEmail', '==', effectiveUserEmail);
    if (scopedTenantId) {
      fallbackQuery = fallbackQuery.where('tenantId', '==', scopedTenantId);
    }
    const fallbackSnapshot = await safeGetSnapshot(fallbackQuery.get(), reportError);
    agreement = resolveLatestAgreement(fallbackSnapshot);
  }

  const initialBranchId =
    normalizeString(userDoc?.branchId) ||
    normalizeString(onboardingRequest?.branchId) ||
    normalizeString(agreement?.branchId) ||
    normalizeString(agreement?.franchiseId) ||
    normalizeString(franchiseOwner?.franchiseId) ||
    null;

  const initialTenantId =
    normalizeString(userDoc?.tenantId) ||
    normalizeString(onboardingRequest?.tenantId) ||
    normalizeString(agreement?.tenantId) ||
    normalizeString(franchiseOwner?.tenantId) ||
    normalizeString(authUser?.tenantId) ||
    null;

  let franchiseOnboarding = null;
  let branchId = initialBranchId;

  if (!branchId && effectiveUserId) {
    let onboardingQuery = db
      .collection('franchiseOnboarding')
      .where('userId', '==', effectiveUserId);
    if (scopedTenantId) {
      onboardingQuery = onboardingQuery.where('tenantId', '==', scopedTenantId);
    }
    franchiseOnboarding = await safeGetFirst(onboardingQuery.limit(1), reportError);
    if (franchiseOnboarding?.id) {
      branchId = franchiseOnboarding.id;
    }
  }

  if (branchId && !franchiseOnboarding) {
    franchiseOnboarding = await safeGetDoc(
      db.collection('franchiseOnboarding').doc(branchId),
      reportError,
    );
  }

  if (
    scopedTenantId &&
    franchiseOnboarding?.tenantId &&
    normalizeString(franchiseOnboarding.tenantId) !== scopedTenantId
  ) {
    reportError(new Error('Franchise onboarding tenant mismatch'));
    franchiseOnboarding = null;
  }

  let franchiseDoc = branchId
    ? await safeGetDoc(db.collection('franchises').doc(branchId), reportError)
    : null;
  if (
    scopedTenantId &&
    franchiseDoc?.tenantId &&
    normalizeString(franchiseDoc.tenantId) !== scopedTenantId
  ) {
    reportError(new Error('Franchise tenant mismatch'));
    franchiseDoc = null;
  }

  return buildBrandingContextFromDocs({
    authUser,
    userDoc,
    onboardingRequest,
    agreement,
    franchiseOwner,
    franchiseDoc,
    franchiseOnboarding,
    tenantIdOverride: initialTenantId,
    branchIdOverride: branchId,
    readError,
  });
};

export const validateBrandingReadiness = (context, brandKitRules = {}) => {
  const missingFields = new Set(context?.missingFields || []);
  const blockingReasons = new Set(context?.blockingReasons || []);

  if (brandKitRules.requirePhone && !context?.userProfile?.phone) {
    missingFields.add('userProfile.phone');
  }
  if (brandKitRules.requireAddress && !context?.branchProfile?.address) {
    missingFields.add('branchProfile.address');
  }
  if (brandKitRules.requireRera && !context?.branchProfile?.reraId) {
    missingFields.add('branchProfile.reraId');
  }

  const isReady = blockingReasons.size === 0 && missingFields.size === 0;

  return {
    isReady,
    missingFields: Array.from(missingFields),
    blockingReasons: Array.from(blockingReasons),
  };
};

export const __testing = {
  resolveFullName,
  resolveEmail,
  buildBrandingContextFromDocs,
};
